/**
 * Shape mapping registry — canonical shapes to draw.io styles.
 * Also provides reverse mapping (draw.io style patterns → canonical shapes).
 */

import { NodeShape } from './types';

export interface ShapeEntry {
  defaultWidth: number;
  defaultHeight: number;
  flowchartStyle?: string;
  activityStyle?: string;
}

/** Forward registry: canonical shape → dimensions + draw.io styles */
export const SHAPE_REGISTRY: Record<NodeShape, ShapeEntry> = {
  [NodeShape.Process]: {
    defaultWidth: 105,
    defaultHeight: 40,
    flowchartStyle: 'rounded=0;whiteSpace=wrap;html=1;',
    activityStyle:
      'html=1;align=center;verticalAlign=middle;rounded=1;absoluteArcSize=1;arcSize=10;dashed=0;whiteSpace=wrap;fontSize=10;',
  },
  [NodeShape.Decision]: {
    defaultWidth: 107.5,
    defaultHeight: 70,
    flowchartStyle: 'shape=mxgraph.flowchart.decision;whiteSpace=wrap;html=1;',
    activityStyle:
      'strokeWidth=1;html=1;shape=mxgraph.flowchart.decision;whiteSpace=wrap;fontSize=10;',
  },
  [NodeShape.StartCircle]: {
    defaultWidth: 30,
    defaultHeight: 30,
    activityStyle: 'ellipse;fillColor=strokeColor;html=1;',
  },
  [NodeShape.EndCircle]: {
    defaultWidth: 30,
    defaultHeight: 30,
    activityStyle: 'ellipse;html=1;shape=endState;fillColor=strokeColor;',
  },
  [NodeShape.Datastore]: {
    defaultWidth: 80,
    defaultHeight: 40,
    flowchartStyle: 'shape=datastore;whiteSpace=wrap;html=1;fontSize=10;',
    activityStyle: 'shape=datastore;whiteSpace=wrap;html=1;fontSize=10;',
  },
  [NodeShape.ForkBar]: {
    defaultWidth: 80,
    defaultHeight: 5,
    activityStyle:
      'html=1;points=[];perimeter=orthogonalPerimeter;fillColor=strokeColor;direction=south;',
  },
  [NodeShape.JoinBar]: {
    defaultWidth: 80,
    defaultHeight: 5,
    activityStyle:
      'html=1;points=[];perimeter=orthogonalPerimeter;fillColor=strokeColor;direction=south;',
  },
  [NodeShape.MergeDiamond]: {
    defaultWidth: 30,
    defaultHeight: 30,
    activityStyle: 'rhombus;',
  },
  [NodeShape.Subroutine]: {
    defaultWidth: 120,
    defaultHeight: 40,
    flowchartStyle:
      'shape=mxgraph.flowchart.predefined_process;whiteSpace=wrap;html=1;',
    activityStyle:
      'shape=mxgraph.flowchart.predefined_process;whiteSpace=wrap;html=1;',
  },
  [NodeShape.ClassBox]: {
    defaultWidth: 160,
    defaultHeight: 80,
    flowchartStyle: 'swimlane;fontStyle=1;align=center;startSize=26;html=1;',
  },
  [NodeShape.ErEntity]: {
    defaultWidth: 200,
    defaultHeight: 100,
    flowchartStyle: 'shape=table;startSize=30;container=1;collapsible=1;childLayout=tableLayout;html=1;',
  },
  [NodeShape.Stadium]: {
    defaultWidth: 105,
    defaultHeight: 40,
    flowchartStyle: 'rounded=1;whiteSpace=wrap;html=1;arcSize=50;',
  },
};

/** Edge styles */
export const EDGE_STYLE =
  'edgeStyle=orthogonalEdgeStyle;rounded=0;orthogonalLoop=1;jettySize=auto;html=1;';
export const EDGE_STYLE_DASHED = EDGE_STYLE + 'dashed=1;';
export const LABEL_STYLE =
  'edgeLabel;html=1;align=center;verticalAlign=middle;resizable=0;points=[];';

/** Container / lane styles */
export const CONTAINER_STYLE =
  'swimlane;childLayout=stackLayout;resizeParent=1;resizeParentMax=0;startSize=20;html=1;fontSize=10;';
export const LANE_STYLE = 'swimlane;startSize=20;html=1;';

/** Reverse registry: regex patterns matching draw.io styles → canonical shape */
export const REVERSE_STYLE_MAP: Array<{ pattern: RegExp; shape: NodeShape }> = [
  { pattern: /shape=endState/, shape: NodeShape.EndCircle },
  {
    pattern: /ellipse.*fillColor=strokeColor/,
    shape: NodeShape.StartCircle,
  },
  {
    pattern: /shape=mxgraph\.flowchart\.decision/,
    shape: NodeShape.Decision,
  },
  { pattern: /rhombus/, shape: NodeShape.MergeDiamond },
  { pattern: /shape=datastore/, shape: NodeShape.Datastore },
  {
    pattern: /perimeter=orthogonalPerimeter.*fillColor=strokeColor/,
    shape: NodeShape.ForkBar,
  },
  {
    pattern: /shape=mxgraph\.flowchart\.predefined_process/,
    shape: NodeShape.Subroutine,
  },
  { pattern: /swimlane;fontStyle=1/, shape: NodeShape.ClassBox },
  { pattern: /shape=table/, shape: NodeShape.ErEntity },
  { pattern: /arcSize=50/, shape: NodeShape.Stadium },
];

/** Look up canonical shape from a draw.io style string */
export function shapeFromStyle(style: string): NodeShape {
  for (const { pattern, shape } of REVERSE_STYLE_MAP) {
    if (pattern.test(style)) return shape;
  }
  return NodeShape.Process;
}
